import { categoryTraversal } from '@/Utility/categories';
import { HubTemplateEnum } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import { ChevronRight } from '@mui/icons-material';
import { Grid, Menu, MenuItem, type MenuProps, Tooltip } from '@mui/material';
import { createLink, useLocation, useSearch } from '@tanstack/react-router';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useMemo } from 'react';
import { PAGE_LAYOUT } from '../Page';
import Chips from './Chips';
import SearchBar from './SearchBar';

import styles from './TopBar.module.css';

const escapeHtml = (unsafe: string | undefined | null) => {
  if (!unsafe) return '';
  const doc = new DOMParser().parseFromString(unsafe, 'text/html');
  return doc.documentElement.textContent;
};

const MenuItemLink = createLink(MenuItem);

type Props = {
  disabled?: boolean;
};

const TopBar = (props: Props) => {
  const categories = useAppStore((state) => state.categories);
  const znglists = useAppStore((state) => state.znglists);
  const hubs = useAppStore((state) => state.hubs);

  const location = useLocation();

  const menuProps: Partial<MenuProps> = useMemo(
    () => ({
      slotProps: {
        paper: { className: styles.navMenuPaper },
      },
      MenuListProps: { className: styles.navMenuList },
      disableAutoFocus: true,
      disableAutoFocusItem: true,
      disableRestoreFocus: true,
    }),
    []
  );

  const search = useSearch({
    strict: false,
  });

  return (
    <Grid container className="top-bar" sx={{ mt: 1 }} {...PAGE_LAYOUT.centerProps}>
      <Grid
        order={{ xs: 2, md: 1 }}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'start',
          gap: 0.75,
        }}
        {...PAGE_LAYOUT.leftProps}
      >
        {categories?.edges
          ?.sort((a, b) => a?.order - b?.order)
          .map((node) => {
            return (
              <PopupState key={node.id} variant="popover" popupId={node.slug}>
                {(popupState) => (
                  <>
                    <button
                      type="button"
                      {...bindTrigger(popupState)}
                      className={`${styles.navButton}`}
                      aria-label={`${node.name} category dropdown`}
                    >
                      {escapeHtml(node.name)}
                      <ChevronRight className={`${styles.chevron}`} />
                    </button>
                    <Menu {...bindMenu(popupState)} {...menuProps}>
                      {node.children?.edges
                        ?.sort((a, b) => a?.order - b?.order)
                        .map((child) => {
                          const selectedCategoryFromParent =
                            categoryTraversal(node, (c) => {
                              return search?.categories?.includes?.(c?.id ?? '') ? c : false;
                            }).filter(Boolean)?.[0] || null;

                          const disabled = search?.categories?.includes?.(child?.id ?? '');

                          const title = child?.name?.length > 25 ? `${child?.name?.substring(0, 25)}...` : child?.name;

                          return (
                            <MenuItemLink
                              disabled={props?.disabled || disabled}
                              key={child.id}
                              onClick={popupState.close}
                              className={`${styles.navMenuItem}`}
                              {...{ [`order-${child.order}`]: '' }}
                              // link props
                              to="/"
                              search={({ categories, c, ...prev }) => {
                                let searchCats = [...new Set(categories || [])];
                                if (searchCats.includes(child?.id ?? '')) {
                                  return { ...prev, categories: searchCats };
                                }

                                if (child.parent?.id === selectedCategoryFromParent?.parent?.id) {
                                  searchCats = searchCats.filter((cat) => cat !== selectedCategoryFromParent?.id);
                                }

                                searchCats.push(child?.id ?? '');

                                return {
                                  ...prev,
                                  categories: searchCats,
                                };
                              }}
                              preload="intent"
                            >
                              <Tooltip
                                title={escapeHtml(child?.name ?? '')}
                                enterDelay={500}
                                disableHoverListener={(child?.name?.length ?? 0) <= 25}
                              >
                                <p className={`${styles.navMenuItemLink}`}>{escapeHtml(title)}</p>
                              </Tooltip>
                            </MenuItemLink>
                          );
                        })}
                    </Menu>
                  </>
                )}
              </PopupState>
            );
          })}

        <PopupState variant="popover" popupId="hub">
          {(popupState) => (
            <>
              <button
                type="button"
                {...bindTrigger(popupState)}
                className={`${styles.navButton}`}
                hidden={!hubs?.edges?.length}
              >
                Hubs
                <ChevronRight className={`${styles.chevron}`} />
              </button>
              <Menu {...bindMenu(popupState)} {...menuProps}>
                {hubs?.edges
                  ?.toSorted(
                    (a, b) =>
                      (Number(a?.node?.postOrder) || Number.POSITIVE_INFINITY) -
                      (Number(b?.node?.postOrder) || Number.POSITIVE_INFINITY)
                  )
                  .map((hub) => {
                    const node = hub?.node;

                    const disabled = node?.slug ? location.pathname.includes(node?.slug) : false;
                    const title =
                      (node?.title ?? '')?.length > 25 ? `${node?.title?.substring(0, 25)}...` : node?.title;

                    return (
                      <MenuItemLink
                        key={node?.slug}
                        className={`${styles.navMenuItem}`}
                        disabled={props?.disabled || disabled}
                        LinkComponent="a"
                        // link props
                        to="/hub/$slug"
                        params={{ slug: node?.slug! }}
                        {...(node?.template === HubTemplateEnum.HomeLandingPage
                          ? { preload: 'intent' }
                          : { target: '_blank', rel: 'noreferrer noopener' })}
                        onClick={(e) => {
                          popupState.close(e);

                          if (node?.template === HubTemplateEnum.HomeLandingPage) {
                            return;
                          }

                          //  open link in new tab
                          window.open(`/hub/${node?.slug}`, '_blank');
                        }}
                      >
                        <Tooltip
                          title={node?.title ?? ''}
                          enterDelay={500}
                          disableHoverListener={(node?.title?.length ?? 0) <= 25}
                        >
                          <p className={`${styles.navMenuItemLink}`}>{escapeHtml(title)}</p>
                        </Tooltip>
                      </MenuItemLink>
                    );
                  })}
              </Menu>
            </>
          )}
        </PopupState>

        <PopupState variant="popover" popupId="znglists">
          {(popupState) => (
            <>
              <button
                type="button"
                {...bindTrigger(popupState)}
                className={`${styles.navButton}`}
                hidden={!znglists?.edges?.length}
              >
                Znglists
                <ChevronRight className={`${styles.chevron}`} />
              </button>
              <Menu {...bindMenu(popupState)} {...menuProps}>
                {znglists?.edges
                  ?.toSorted((a, b) => (a?.node?.znglist?.order ?? 0) - (b?.node?.znglist?.order ?? 0))
                  .map((znglist) => {
                    const node = znglist?.node;
                    // trim the title to 25 characters
                    const title =
                      (node?.title?.length ?? 0) > 25 ? `${node?.title?.substring?.(0, 25)}...` : node?.title;

                    const disabled = node?.slug ? location.pathname.includes(node?.slug) : false;

                    return (
                      <MenuItemLink
                        key={node?.slug}
                        onClick={popupState.close}
                        className={`${styles.navMenuItem}`}
                        disabled={props?.disabled || disabled}
                        // link props
                        to="/znglist/$slug"
                        params={{ slug: node!.slug! }}
                      >
                        <Tooltip
                          title={node?.title ?? ''}
                          enterDelay={500}
                          disableHoverListener={(node?.title?.length ?? 0) <= 25}
                        >
                          <p className={`${styles.navMenuItemLink}`}>{escapeHtml(title)}</p>
                        </Tooltip>
                      </MenuItemLink>
                    );
                  })}
                <MenuItemLink
                  onClick={popupState.close}
                  className={`${styles.navMenuItem}`}
                  to="/view-all"
                  disabled={props?.disabled}
                >
                  <Tooltip title="All Assets" enterDelay={500}>
                    <p className={`${styles.navMenuItemLink}`}>All Assets</p>
                  </Tooltip>
                </MenuItemLink>
              </Menu>
            </>
          )}
        </PopupState>
        <Chips disabled={props?.disabled} />
      </Grid>

      <Grid {...PAGE_LAYOUT.rightProps} order={{ xs: 1, md: 2 }}>
        <SearchBar disabled={props?.disabled} />
      </Grid>
    </Grid>
  );
};

export const ExampleTopBarItem = () => {
  const menuProps: Partial<MenuProps> = useMemo(
    () => ({
      slotProps: {
        paper: { className: styles.navMenuPaper },
      },
      MenuListProps: { className: styles.navMenuList },
      disableAutoFocus: true,
      disableAutoFocusItem: true,
      disableRestoreFocus: true,
    }),
    []
  );
  return (
    <PopupState variant="popover" popupId="hub">
      {(popupState) => (
        <>
          <button type="button" {...bindTrigger(popupState)} className={`${styles.navButton}`}>
            Example
            <ChevronRight className={`${styles.chevron}`} />
          </button>
          <Menu {...bindMenu(popupState)} {...menuProps}>
            {[...Array.from({ length: 16 })].map((_, idx) => {
              const title = `Example ${idx + 1}`;
              return (
                <MenuItem key={title} onClick={popupState.close} className={`${styles.navMenuItem}`}>
                  <p className={`${styles.navMenuItemLink}`}>{escapeHtml(title)}</p>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </PopupState>
  );
};

export default TopBar;
